import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchProject } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { projectNav } from '../lib/projectNav';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { useCanonicalProjectRef } from '../hooks/useCanonicalProjectRef';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ProjectSectionNav } from '../components/ProjectSectionNav';
import { PageHeader } from '../components/PageHeader';
import { ChatPanel } from '../components/ChatPanel';
import { ErrorState, LoadingState } from '../components/States';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

export function ProjectChatPage() {
  const { projectId = '', sessionId } = useParams();
  const { wp } = useWorkspacePath();

  const projectQuery = useQuery({
    queryKey: qk.project(projectId),
    queryFn: () => fetchProject(projectId),
    enabled: !!projectId,
  });

  // Keep the URL on the pretty code; the thread keys off the resolved id.
  useCanonicalProjectRef(projectId, projectQuery.data?.code);
  const resolvedId = projectQuery.data?.id ?? projectId;

  useDocumentTitle(
    projectQuery.data ? `Chat · ${projectQuery.data.name}` : 'Chat',
  );

  if (projectQuery.isLoading) return <LoadingState label="Loading chat…" />;
  if (projectQuery.isError) {
    return (
      <ErrorState
        message={errorMessage(projectQuery.error, 'Failed to load project')}
        onRetry={() => projectQuery.refetch()}
      />
    );
  }

  const project = projectQuery.data!;
  const nav = projectNav(project);

  return (
    <div className="space-y-4 pb-16 sm:pb-0">
      <Breadcrumbs
        items={[
          { label: nav.rootLabel, to: wp(nav.rootTo) },
          { label: nav.name, to: wp(nav.selfTo) },
          { label: 'Chat' },
        ]}
      />

      <PageHeader
        title="Chat"
        subtitle={`Talk with the Product Manager for ${project.name}.`}
      />

      <ProjectSectionNav
        kind={project.kind}
        projectRef={project.code ?? project.id}
        active="chat"
      />

      <ChatPanel
        projectId={resolvedId}
        basePath={wp(`${nav.selfTo}/chat`)}
        sessionParam={sessionId}
        projectName={project.name}
      />
    </div>
  );
}
